import {
	List,
	mergeStyles,
	PrimaryButton,
	Stack,
	useTheme,
} from '@fluentui/react';
import {LoadWrapper} from 'components/LoadWrapper';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getListClassNames} from 'styles';
import {KeywordStatus} from 'types';
import {GetWorkbenchItemsVexQuery} from '../../hooks/workbenchItems.generated';
import {RegulationHeaderRow, WorkbenchHeader} from '../Headers';
import {useDashboardQueries} from 'features/Dashboard/hooks/useDashboardQueries';
import {useUserContext} from 'authentication/UserContext';
import {v4 as uuidv4} from 'uuid';
import {FilterItem} from 'components';
import {useVexWorkbenchItemsFilter} from './useVexWorkbenchItemsFilter';
import {VexWorkbenchItemCell} from './VexWorkbenchItemCell';

export type DashboardCardProps = {
	halfHeight?: boolean;
	itemCountLoaded?: (count: number) => void;
	isVkoApproverDashboard?: boolean;
};

type VexWorkbenchItemsProps = {
	keywordStatus:
		| KeywordStatus.Finalized
		| KeywordStatus.Accepted
		| KeywordStatus.New;
} & DashboardCardProps;

export type VexWorkbenchItem =
	GetWorkbenchItemsVexQuery['workbenchRegulatoryDocumentParagraphs'][0];

export const VexWorkbenchItems: React.FC<VexWorkbenchItemsProps> = ({
	halfHeight = false,
	keywordStatus,
	itemCountLoaded,
}) => {
	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'VexWorkbenchItems',
	});
	const getTitle = useCallback(
		(keywordStatus: any) => {
			switch (keywordStatus) {
				case KeywordStatus.Accepted:
					return t('InProcessTitle');
				case KeywordStatus.Finalized:
					return t('CompletedTitle');
				case KeywordStatus.New:
					return t('AssessResponsibilityTitle');
				default:
					return t('DefaultTitle');
			}
		},
		[t],
	);
	const proportionTable = useMemo(
		() =>
			getTitle(keywordStatus) === t('CompletedTitle')
				? [60, 20, 20]
				: [40, 20, 20, 20],
		[getTitle, keywordStatus, t],
	);

	const {getWorkbenchItemsVexQuery} = useDashboardQueries();
	const {myVexClusters} = useUserContext();
	const {data, loading} = getWorkbenchItemsVexQuery(keywordStatus);
	const [itemListKey, setItemListKey] = useState(uuidv4());
	const [filterPanel, setFilterPanel] = useState(false);

	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);

	useEffect(() => {
		if (myVexClusters?.length > 0) {
			setItemListKey(uuidv4());
		}
	}, [myVexClusters]);

	const items: VexWorkbenchItem[] = useMemo(
		() => data?.workbenchRegulatoryDocumentParagraphs ?? [],
		[data],
	);

	const {
		filteredItems,
		filters,
		setFilters,
		filterLegalAreas,
		filterStatuses,
		filterVexClusters,
	} = useVexWorkbenchItemsFilter(items);

	useEffect(() => {
		if (!loading && data?.workbenchRegulatoryDocumentParagraphs) {
			itemCountLoaded?.(data.workbenchRegulatoryDocumentParagraphs?.length);
		}
	}, [data, loading]);

	const setSelectedFilters = (filters: any) => {
		setFilters(filters);
	};

	const filterPanelContent = useMemo(
		() => (
			<div
				className={mergeStyles(
					classNames.filterPanelContainer,
					classNames.container,
				)}
				data-is-scrollable
			>
				<div style={{height: 310}}>
					<Stack
						horizontal
						tokens={{childrenGap: 10}}
						style={{
							height: 50,
							bottom: 10,
							width: '100%',
							justifyContent: 'flex-end',
						}}
					>
						<PrimaryButton
							text={t('ClearFilters')}
							onClick={() => {
								setFilters([]);
								setFilterPanel(false);
							}}
						/>
						<PrimaryButton
							text={t('ApplyFilters')}
							onClick={() => setFilterPanel(false)}
						/>
					</Stack>
					<Stack
						horizontal
						style={{justifyContent: 'space-between', height: '100%'}}
					>
						<Stack.Item>
							<FilterItem
								col={{key: 'LegalArea', name: t('LegalArea'), minWidth: 10}}
								blanksExists={false}
								selectedFilters={filters}
								setSelectedFilters={setSelectedFilters}
								getFilterFn={(match: any) => (item: any) => true}
								getBlankFilterFn={(item: any) => true}
								sortedValues={filterLegalAreas}
								expandedByDefault={true}
							/>
						</Stack.Item>
						<Stack.Item>
							<FilterItem
								col={{key: 'Status', name: t('Status'), minWidth: 10}}
								blanksExists={false}
								selectedFilters={filters}
								setSelectedFilters={setSelectedFilters}
								getFilterFn={(match: any) => (item: any) => true}
								getBlankFilterFn={(item: any) => true}
								sortedValues={filterStatuses}
								expandedByDefault={true}
							/>
						</Stack.Item>
						<Stack.Item>
							<FilterItem
								col={{key: 'VexCluster', name: t('VexCluster'), minWidth: 10}}
								blanksExists={false}
								selectedFilters={filters}
								setSelectedFilters={setSelectedFilters}
								getFilterFn={(match: any) => (item: any) => true}
								getBlankFilterFn={(item: any) => true}
								sortedValues={filterVexClusters}
								expandedByDefault={true}
							/>
						</Stack.Item>
					</Stack>
				</div>
			</div>
		),
		[
			classNames,
			filters,
			filterLegalAreas,
			filterStatuses,
			filterVexClusters,
			t,
		],
	);

	return (
		<div>
			<WorkbenchHeader
				halfHeight
				title={getTitle(keywordStatus)}
				items={
					keywordStatus === KeywordStatus.Finalized ? undefined : items.length
				}
				filterIconCallback={() => setFilterPanel(!filterPanel)}
				anyFilterSet={filters.length > 0}
			/>
			{filterPanel ? (
				filterPanelContent
			) : (
				<div className={classNames.container} data-is-scrollable>
					<RegulationHeaderRow
						proportionTable={proportionTable}
						columnKey={getTitle(keywordStatus)}
						t={t}
					/>
					<LoadWrapper loading={loading}>
						<List
							key={itemListKey}
							items={filteredItems}
							onRenderCell={(item: any) => (
								<VexWorkbenchItemCell
									keywordStatus={keywordStatus}
									item={item}
									columnKey={getTitle(keywordStatus)}
									proportionTable={proportionTable}
									halfHeight={halfHeight}
								/>
							)}
						/>
					</LoadWrapper>
				</div>
			)}
		</div>
	);
};
